/**
 * Hala CRM - Supervisor View
 * Monitoring interface with tabs for managing group requests
 * Access: Admin, Supervisor ONLY
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Monitor, FileText, Inbox, UserCheck, CheckCircle, AlertTriangle,
  Clock, RefreshCw, User, ChevronRight, Users, ArrowLeftRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const STATUS_CONFIG = {
  NEW: { label: 'جديد', color: COLORS.info, bg: COLORS.infoLight },
  IN_POOL: { label: 'في المجمع', color: COLORS.warning, bg: COLORS.warningLight },
  ASSIGNED: { label: 'مُسند', color: COLORS.primary, bg: `${COLORS.primary}20` },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bg: `${COLORS.gold}20` },
  COMPLETED: { label: 'مكتمل', color: COLORS.success, bg: COLORS.successLight },
  CANCELLED: { label: 'ملغي', color: COLORS.error, bg: COLORS.errorLight },
};

const TABS = [
  { id: 'all', label: 'الكل', icon: FileText },
  { id: 'pool', label: 'في المجمع', icon: Inbox },
  { id: 'assigned', label: 'مُسندة', icon: UserCheck },
  { id: 'completed', label: 'مكتملة', icon: CheckCircle },
];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, color: COLORS.textMuted, bg: COLORS.bgDark };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '20px',
      backgroundColor: config.bg,
      color: config.color,
      fontSize: '12px',
      fontWeight: 500,
    }}>
      {config.label}
    </span>
  );
}

function ReassignModal({ request, users, onClose, onReassign }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedUser) return;
    setLoading(true);
    await onReassign(request.request_id, selectedUser);
    setLoading(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        padding: '24px',
        width: '400px',
        maxWidth: '90%',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
          إعادة تعيين الطلب
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: COLORS.textMuted }}>
          اختر المستخدم الذي تريد تعيين الطلب له
        </p>
        
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            marginBottom: '20px',
          }}
        >
          <option value="">اختر مستخدم...</option>
          {users.map(u => (
            <option key={u.user_id} value={u.user_id}>
              {u.name_ar} ({u.role_id === 'supervisor' ? 'مشرف' : u.role_id === 'group_user' ? 'مستخدم' : u.role_id})
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              backgroundColor: COLORS.white,
              color: COLORS.text,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            إلغاء
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedUser || loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              fontSize: '14px',
              fontWeight: 500,
              cursor: !selectedUser || loading ? 'not-allowed' : 'pointer',
              opacity: !selectedUser || loading ? 0.6 : 1,
            }}
          >
            {loading ? 'جاري التعيين...' : 'تعيين'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CRMSupervisorPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupUsers, setGroupUsers] = useState([]);
  const [reassignModal, setReassignModal] = useState(null);

  // Check access
  const hasAccess = user?.role_id === 'admin' || user?.role_id === 'supervisor';

  const fetchData = useCallback(async () => {
    if (!user || !hasAccess) return;
    setLoading(true);
    
    try {
      const [viewRes, usersRes] = await Promise.all([
        axios.get(`${API}/requests/supervisor-view/${user.user_id}?tab=${activeTab}`, { headers: getCRMHeaders() }),
        axios.get(`${API}/requests/group-users/${user.user_id}`, { headers: getCRMHeaders() }),
      ]);
      setData(viewRes.data);
      setGroupUsers(usersRes.data || []);
    } catch (e) {
      console.error('Failed to fetch supervisor data:', e);
    } finally {
      setLoading(false);
    }
  }, [user, hasAccess, activeTab, getCRMHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReassign = async (requestId, newOwnerId) => {
    try {
      await axios.post(
        `${API}/requests/${requestId}/reassign`,
        { new_owner_id: newOwnerId, performed_by: user.user_id },
        { headers: getCRMHeaders() }
      );
      fetchData();
    } catch (e) {
      console.error('Failed to reassign:', e);
      alert(e.response?.data?.detail || 'فشل إعادة التعيين');
    }
  };

  // Access denied for non-supervisors
  if (!hasAccess) {
    return (
      <div style={{ 
        direction: 'rtl', 
        padding: '60px', 
        textAlign: 'center',
        backgroundColor: COLORS.errorLight,
        borderRadius: '12px',
        margin: '40px',
      }}>
        <AlertTriangle size={48} color={COLORS.error} style={{ marginBottom: '16px' }} />
        <h2 style={{ color: COLORS.error, margin: '0 0 8px' }}>غير مصرح بالوصول</h2>
        <p style={{ color: COLORS.textMuted, margin: 0 }}>هذه الصفحة متاحة للمشرفين والمدراء فقط</p>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
            إدارة الطلبات
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
            مراقبة وإدارة طلبات المجموعة
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          تحديث
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        backgroundColor: COLORS.bgDark,
        padding: '4px',
        borderRadius: '12px',
        marginBottom: '24px',
      }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const count = data?.stats?.[tab.id] || 0;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: isActive ? COLORS.white : 'transparent',
                color: isActive ? COLORS.primary : COLORS.textMuted,
                fontSize: '14px',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              <Icon size={16} />
              {tab.label}
              <span style={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: isActive ? COLORS.primary : COLORS.border,
                color: isActive ? COLORS.white : COLORS.textMuted,
                fontSize: '12px',
                fontWeight: 600,
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Requests Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            جاري التحميل...
          </div>
        ) : !data?.requests?.length ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Monitor size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.textMuted} />
            <p style={{ margin: 0, color: COLORS.textMuted }}>لا توجد طلبات في هذا التبويب</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  رقم الطلب
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  مقدم الطلب
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  الخدمة
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  الحالة
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  المالك الحالي
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  عمر الطلب
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  إجراءات
                </th>
              </tr>
            </thead>
            <tbody>
              {data.requests.map((req) => (
                <tr
                  key={req.request_id}
                  style={{
                    borderBottom: `1px solid ${COLORS.border}`,
                    backgroundColor: req.is_delayed ? COLORS.errorLight : 'transparent',
                  }}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {req.is_delayed && (
                        <AlertTriangle size={16} color={COLORS.error} title="متأخر" />
                      )}
                      <span 
                        style={{ fontSize: '13px', fontWeight: 500, color: COLORS.primary, cursor: 'pointer' }}
                        onClick={() => navigate(`/crm/request/${req.request_id}`)}
                      >
                        {req.request_id?.substring(0, 12)}...
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', color: COLORS.text }}>
                      {req.applicant_name || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', color: COLORS.text }}>
                      {req.service_name_ar || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <StatusBadge status={req.status} />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        backgroundColor: COLORS.bgDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <User size={14} color={COLORS.textMuted} />
                      </div>
                      <span style={{ fontSize: '13px', color: COLORS.text }}>
                        {req.owner_name || '-'}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} color={req.is_delayed ? COLORS.error : COLORS.textMuted} />
                      <span style={{ 
                        fontSize: '12px', 
                        color: req.is_delayed ? COLORS.error : COLORS.textMuted,
                        fontWeight: req.is_delayed ? 500 : 400,
                      }}>
                        {req.time_since || '-'}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => navigate(`/crm/request/${req.request_id}`)}
                        title="عرض التفاصيل"
                        style={{
                          padding: '6px 10px',
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: '6px',
                          backgroundColor: COLORS.white,
                          cursor: 'pointer',
                        }}
                      >
                        <ChevronRight size={16} color={COLORS.textMuted} />
                      </button>
                      {req.status !== 'COMPLETED' && req.status !== 'CANCELLED' && (
                        <button
                          onClick={() => setReassignModal(req)}
                          title="إعادة تعيين"
                          style={{
                            padding: '6px 10px',
                            border: `1px solid ${COLORS.primary}`,
                            borderRadius: '6px',
                            backgroundColor: `${COLORS.primary}10`,
                            cursor: 'pointer',
                          }}
                        >
                          <ArrowLeftRight size={16} color={COLORS.primary} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delay Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: COLORS.bgDark,
        borderRadius: '8px',
        fontSize: '13px',
        color: COLORS.textMuted,
      }}>
        <AlertTriangle size={16} color={COLORS.error} />
        <span>الطلبات المتأخرة (أكثر من 24 ساعة) تظهر باللون الأحمر</span>
      </div>

      {/* Reassign Modal */}
      {reassignModal && (
        <ReassignModal
          request={reassignModal}
          users={groupUsers}
          onClose={() => setReassignModal(null)}
          onReassign={handleReassign}
        />
      )}
    </div>
  );
}
